import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import {
  Wrench,
  Database,
  Trash2,
  Zap,
  Clock,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";

const SystemMaintenance = () => {
  const [cleanupDays, setCleanupDays] = useState(90);
  const [operationStatus, setOperationStatus] = useState(null);
  const [loading, setLoading] = useState({
    cleanup: false,
    optimize: false,
  });

  const handleCleanupOldData = async () => {
    setLoading(prev => ({ ...prev, cleanup: true }));
    setOperationStatus(null);
    
    try {
      const token = localStorage.getItem("superAdminToken");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/super-admin/system/cleanup`,
        { days: cleanupDays },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setOperationStatus({
        type: "success",
        message: response.data.message,
        data: response.data.data
      });
    } catch (error) {
      setOperationStatus({
        type: "error",
        message: error.response?.data?.message || "حدث خطأ أثناء تنظيف البيانات"
      });
    } finally {
      setLoading(prev => ({ ...prev, cleanup: false }));
    }
  };

  const handleOptimizeDatabases = async () => {
    setLoading(prev => ({ ...prev, optimize: true }));
    setOperationStatus(null);
    
    try {
      const token = localStorage.getItem("superAdminToken");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/super-admin/system/optimize`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setOperationStatus({
        type: "success",
        message: response.data.message
      });
    } catch (error) {
      setOperationStatus({
        type: "error",
        message: error.response?.data?.message || "حدث خطأ أثناء تحسين قواعد البيانات"
      });
    } finally {
      setLoading(prev => ({ ...prev, optimize: false }));
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">صيانة النظام</h1>
        <p className="text-slate-600 mt-1">
          أدوات لتنظيف وتحسين أداء النظام
        </p>
      </div>

      {operationStatus && (
        <Alert variant={operationStatus.type === "success" ? "default" : "destructive"}>
          {operationStatus.type === "success" ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertTitle>
            {operationStatus.type === "success" ? "نجاح" : "خطأ"}
          </AlertTitle>
          <AlertDescription>
            {operationStatus.message}
            {operationStatus.data && (
              <div className="mt-2 text-sm">
                <p>سجلات النشاطات المحذوفة: {operationStatus.data.activity_logs}</p>
                <p>النسخ الاحتياطية القديمة المحذوفة: {operationStatus.data.old_backups}</p>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cleanup Old Data */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              تنظيف البيانات القديمة
            </CardTitle>
            <CardDescription>
              حذف السجلات القديمة لتحسين أداء النظام
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="days">حذف السجلات الأقدم من (أيام)</Label>
              <Input
                id="days"
                type="number"
                value={cleanupDays}
                onChange={(e) => setCleanupDays(parseInt(e.target.value) || 90)}
                min="1"
                max="365"
              />
            </div>
            
            <p className="text-sm text-slate-600">
              سيتم حذف سجلات النشاطات والنسخ الاحتياطية القديمة لتحرير مساحة التخزين
            </p>
            
            <Button
              onClick={handleCleanupOldData}
              disabled={loading.cleanup}
              className="w-full flex items-center gap-2"
              variant="destructive"
            >
              {loading.cleanup ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  جاري التنظيف...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  تنظيف البيانات
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Optimize Databases */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              تحسين قواعد البيانات
            </CardTitle>
            <CardDescription>
              تحسين أداء قواعد البيانات لجميع المستأجرين
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900">ما الذي يقوم به هذا الخيار؟</h4>
              <p className="text-sm text-blue-800 mt-1">
                يقوم بتحسين جداول قواعد البيانات لجميع مكاتب المحاماة لتحسين الأداء وتقليل حجم التخزين
              </p>
            </div>
            
            <Button
              onClick={handleOptimizeDatabases}
              disabled={loading.optimize}
              className="w-full flex items-center gap-2"
            >
              {loading.optimize ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  جاري التحسين...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  تحسين قواعد البيانات
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            نصائح الصيانة
          </CardTitle>
          <CardDescription>
            أفضل الممارسات للحفاظ على صحة النظام
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-slate-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>قم بتنظيف البيانات القديمة بشكل دوري (كل 3 أشهر)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>قم بتحسين قواعد البيانات بعد كل عملية تنظيف كبيرة</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>راقب مساحة التخزين بانتظام لتجنب النفاد</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>راجع سجلات النشاطات بانتظام للكشف عن أي أنشطة مشبوهة</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemMaintenance;